import { RoomSession } from '../../models/Room'
import { IntegrationEmail } from '../../models/Email'
import { Task } from '../../models/MeetingSupport'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useLazyGetRoomSessionQuery } from '../../store/media/media'
import { useLazyGetEmailQuery } from '../../store/integrations/integrations'
import { useCallback, useEffect, useState } from 'react'
import { addTasks, deleteTask, resetTasks, selectTasks, setTasks, setTasksLoading, updateTask } from '../../store/task/task'
import {
  CreateTaskPayload,
  GetTasksFilters,
  UpdateTaskPayload,
  useCreateTasksBatchMutation,
  useDeleteTaskMutation,
  useLazyGetTasksQuery,
  useUpdateTaskMutation,
} from '../../store/meetingSupport/meetingSupport'

export enum TaskSourceType {
  MEETING = 'meeting',
  EMAIL = 'email',
  USER = 'user',
  CONTACT = 'contact',
}

interface BaseTaskSource {
  sourceKey: string
}

interface MeetingTaskSource extends BaseTaskSource {
  type: 'meeting'
  source: RoomSession
}

interface EmailTaskSource extends BaseTaskSource {
  type: 'email'
  source: IntegrationEmail
}

export type TaskSource = MeetingTaskSource | EmailTaskSource

export const useTaskSources = () => {
  const accountId = useAppSelector(selectAccountId)
  const tasks = useAppSelector(selectTasks)

  const [triggerGetRoomSession] = useLazyGetRoomSessionQuery()
  const [triggerGetEmail] = useLazyGetEmailQuery()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [taskSources, setTaskSources] = useState<TaskSource[]>([])

  const resolveMeetingTaskSource = useCallback(
    async (roomId: string, sessionId: string): Promise<MeetingTaskSource | null> => {
      try {
        const { data: roomSessionResponse } = await triggerGetRoomSession({ accountId, roomId, sessionId }, true)
        if (!roomSessionResponse || !roomSessionResponse.data) {
          return null
        }
        return { sourceKey: `meeting|${roomId}|${sessionId}`, type: 'meeting', source: roomSessionResponse.data }
      } catch (e) {
        console.error(e)
      }
      return null
    },
    [accountId, triggerGetRoomSession],
  )

  const resolveEmailTaskSource = useCallback(
    async (emailConnector: string, threadId: string, emailId: string): Promise<EmailTaskSource | null> => {
      try {
        const { data: email } = await triggerGetEmail(
          {
            accountId,
            emailConnector,
            emailId,
          },
          true,
        )
        if (!email) {
          return null
        }
        return { sourceKey: `email|${emailConnector}|${threadId}|${emailId}`, type: 'email', source: email }
      } catch (e) {
        console.error(e)
      }
      return null
    },
    [accountId, triggerGetEmail],
  )

  const getTaskSourceResolver = useCallback(
    (task: Task): Promise<TaskSource | null> => {
      if (task.type === 'meeting') {
        const { roomId, sessionId } = task
        return resolveMeetingTaskSource(roomId, sessionId)
      }
      const { emailConnector, threadId, emailId } = task
      return resolveEmailTaskSource(emailConnector, threadId, emailId)
    },
    [resolveEmailTaskSource, resolveMeetingTaskSource],
  )

  useEffect(() => {
    const resolveTaskSources = async (taskSourceResolvers: Promise<TaskSource | null>[]) => {
      setIsLoading(true)
      try {
        const taskSourceResolverResponses = await Promise.all(taskSourceResolvers)
        const resolvedTaskSources = taskSourceResolverResponses.reduce<TaskSource[]>((acc, response) => (response ? [...acc, response] : acc), [])
        if (!resolvedTaskSources.length) {
          return
        }
        setTaskSources((prev) => [...prev, ...resolvedTaskSources])
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    }

    const existingSourceKeys = taskSources.map(({ sourceKey }) => sourceKey)

    const taskSourceDescriptors = tasks.reduce<
      {
        sourceKey: string
        resolver: Promise<TaskSource | null>
      }[]
    >((acc, task) => {
      const sourceKey = getTaskSourceKey(task)
      const isUnique = !acc.find((e) => e.sourceKey === sourceKey)
      if (existingSourceKeys.includes(sourceKey) || !isUnique) {
        return acc
      }
      const taskSourceResolver = getTaskSourceResolver(task)
      return [...acc, { sourceKey, resolver: taskSourceResolver }]
    }, [])

    const taskSourceResolvers = taskSourceDescriptors.map(({ resolver }) => resolver)
    if (!taskSourceResolvers.length) {
      return
    }

    resolveTaskSources(taskSourceResolvers)
  }, [getTaskSourceResolver, taskSources, tasks])

  return { taskSources, isLoading }
}

export const getTaskSourceKey = (task: Task) => {
  const { type } = task
  const sourceParts = type === 'meeting' ? [type, task.roomId, task.sessionId] : [type, task.emailConnector, task.threadId, task.emailId]
  return sourceParts.join('|')
}

const _TASKS_PAGE_SIZE = 5

interface UseUserTasksProps {
  filters: GetTasksFilters
  getFetchTasksFilters: (filters: GetTasksFilters) => GetTasksFilters[]
  taskPageSize?: number
}

export const useUserTasks = ({ filters, getFetchTasksFilters, taskPageSize = _TASKS_PAGE_SIZE }: UseUserTasksProps) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetTasks] = useLazyGetTasksQuery()

  const [pageNumber, setPageNumber] = useState<number>(0)
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)

  const reloadTasks = async () => {
    dispatch(resetTasks())
    setPageNumber(0)
    await fetchAllTasks()
  }

  const fetchTasks = useCallback(
    async (filters: GetTasksFilters, pageNumber: number = 0) => {
      try {
        const { data: tasksResponse } = await triggerGetTasks({
          accountId,
          filters,
          pageRequest: { size: taskPageSize, page: pageNumber },
        })
        if (!tasksResponse) {
          return null
        }
        return tasksResponse
      } catch (e) {
        console.error(e)
      }
      return null
    },
    [accountId, triggerGetTasks, taskPageSize],
  )

  const handleAddMoreTasks = useCallback(async () => {
    const _pageNumber = pageNumber + 1
    setIsLoadingMore(true)
    try {
      const fetchTasksFilters = getFetchTasksFilters(filters)
      const taskResponses = await Promise.all(fetchTasksFilters.map((filters) => fetchTasks(filters, _pageNumber)))
      const filteredTaskResponses = taskResponses.reduce<Task[]>((acc, response) => (response ? [...acc, ...response] : acc), [])
      if (!filteredTaskResponses.length) {
        return
      }
      dispatch(addTasks(filteredTaskResponses))
      setPageNumber(_pageNumber)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoadingMore(false)
    }
  }, [dispatch, fetchTasks, filters, getFetchTasksFilters, pageNumber])

  const fetchAllTasks = useCallback(async () => {
    dispatch(setTasksLoading(true))
    try {
      const fetchTasksFilters = getFetchTasksFilters(filters)
      const taskResponses = await Promise.all(fetchTasksFilters.map((filters) => fetchTasks(filters)))
      const filteredTaskResponses = taskResponses.reduce<Task[]>((acc, response) => (response ? [...acc, ...response] : acc), [])
      dispatch(setTasks(filteredTaskResponses))
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setTasksLoading(false))
      setPageNumber(0)
    }
  }, [dispatch, fetchTasks, filters, getFetchTasksFilters])

  useEffect(() => {
    fetchAllTasks()
  }, [dispatch, fetchAllTasks, fetchTasks, filters, getFetchTasksFilters])

  return { isLoadingMore, handleAddMoreTasks, reloadTasks }
}

export const useTaskMutations = () => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const tasks = useAppSelector(selectTasks)
  const [triggerCreateTasksBatch, { isLoading: isCreatingTasks }] = useCreateTasksBatchMutation()
  const [triggerUpdateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation()
  const [triggerDeleteTask, { isLoading: isDeletingTask }] = useDeleteTaskMutation()
  const createTasks = async (tasks: CreateTaskPayload[]) => {
    const { data: createdTasks } = await triggerCreateTasksBatch({ accountId, payload: tasks })
    if (!createdTasks) {
      return
    }
    dispatch(addTasks(createdTasks))
    return createdTasks
  }

  const updateTaskById = async (taskId: string, task: UpdateTaskPayload) => {
    const { data: updatedTask } = await triggerUpdateTask({ accountId, taskId, payload: task })
    if (!updatedTask) {
      return
    }
    dispatch(updateTask(updatedTask))
    return updatedTask
  }

  const _deleteTask = async (task: Task) => {
    const { error } = await triggerDeleteTask({ accountId, taskId: task.id })
    if (error) {
      return
    }
    dispatch(deleteTask(task))
  }

  return {
    tasks,
    createTasks,
    isCreatingTasks,
    updateTaskById,
    isUpdatingTask,
    deleteTask: _deleteTask,
    isDeletingTask,
  }
}
